// Token thumbnail component

import { css, html } from 'lit';
import { getBackendUrl } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * Token thumbnail component
 * Displays a small preview of a token with optional border
 */
class TokenThumbnail extends BaseComponent {
  static properties = {
    imageUrl: { type: String, attribute: 'image-url' },
    name: { type: String },
    size: { type: Number },
    selected: { type: Boolean },
    defeated: { type: Boolean },
    hasCircleBorder: { type: Boolean, attribute: 'has-circle-border' },
    borderColor: { type: String, attribute: 'border-color' },
  };

  static styles = css`
    :host {
      display: inline-block;
    }

    .thumbnail-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .thumbnail {
      width: var(--size, 40px);
      height: var(--size, 40px);
      border-radius: 8px;
      border: 2px solid #666;
      object-fit: cover;
      background-color: #fff;
    }

    .thumbnail.circle {
      border-radius: 50%;
      border-color: var(--border-color, #666);
    }

    .thumbnail.selected {
      box-shadow: 0 0 0 3px #0d6efd;
    }

    .thumbnail.defeated {
      opacity: 0.5;
      filter: grayscale(100%);
    }

    .placeholder {
      width: var(--size, 40px);
      height: var(--size, 40px);
      border-radius: 8px;
      border: 2px solid #666;
      background-color: #4a9eff;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: calc(var(--size, 40px) * 0.4);
    }

    .placeholder.circle {
      border-radius: 50%;
      border-color: var(--border-color, #666);
    }
  `;

  constructor() {
    super();
    this.imageUrl = '';
    this.name = '';
    this.size = 40;
    this.selected = false;
    this.defeated = false;
    this.hasCircleBorder = false;
    this.borderColor = '#666';
  }

  get _resolvedImageUrl() {
    if (!this.imageUrl) return '';
    if (this.imageUrl.startsWith('http')) return this.imageUrl;
    const backendUrl = getBackendUrl();
    return backendUrl ? backendUrl + this.imageUrl : this.imageUrl;
  }

  get _initial() {
    return this.name ? this.name.charAt(0).toUpperCase() : '?';
  }

  render() {
    const sizeStyle = `--size: ${this.size}px; --border-color: ${this.borderColor}`;

    if (this._resolvedImageUrl) {
      return html`
        <div class="thumbnail-wrapper" style="${sizeStyle}">
          <img
            class="thumbnail ${this.hasCircleBorder ? 'circle' : ''} ${this.selected ? 'selected' : ''} ${
              this.defeated ? 'defeated' : ''
            }"
            src="${this._resolvedImageUrl}"
            alt="${this.name}"
            @error="${this._handleImageError}"
          />
        </div>
      `;
    }

    return html`
      <div class="thumbnail-wrapper" style="${sizeStyle}">
        <div
          class="placeholder ${this.hasCircleBorder ? 'circle' : ''} ${this.selected ? 'selected' : ''} ${
            this.defeated ? 'defeated' : ''
          }"
        >
          ${this._initial}
        </div>
      </div>
    `;
  }

  _handleImageError() {
    // Force re-render with placeholder
    this.imageUrl = '';
  }
}

customElements.define('token-thumbnail', TokenThumbnail);
